import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/joy';
import { useMediaQuery } from '@mui/material';

import PiPPopup from '../../assets/images/PiP.svg?react';
import PiPButtonRight from '../../assets/images/PiPButtonRight.svg?react';
import PiPHide from '../../assets/images/PiPHide.svg?react';
import PipResize from '../../assets/images/PiPResize.svg?react';
import { LiveLineData, MarkerData, MapType } from '../Mapbox/mapTypes';
import MapDroneView from '../MapDroneView';
import { crewPlayout } from '../playoutQueue';
import SimpleVideoStream from '../SimpleVideoStream';
import { FitMode } from '../VideoScaler';

interface VideoPiPContainerProps {
  isFullscreenVideo: boolean;
  liveLine: LiveLineData;
  markerImages: { [id: string]: MarkerData };
  mapRef: React.MutableRefObject<MapType | null>;
  videoUrl: string;
  onChangeView: () => void;
  initialZoom?: number;
  fitMode?: FitMode;
  onFitModeChange?: (mode: FitMode) => void;
  autoPlayout?: boolean;
  strictFrames?: boolean;
}

const VideoPiPContainer: React.FC<VideoPiPContainerProps> = React.memo(
  ({
    isFullscreenVideo,
    liveLine,
    markerImages,
    mapRef,
    videoUrl,
    onChangeView,
    initialZoom,
    fitMode,
    onFitModeChange,
    autoPlayout = true,
    strictFrames = true
  }) => {
    const { t } = useTranslation();
    const isMobileLandscape = useMediaQuery('(orientation: landscape) and (max-width: 1024px)');

    // Keep base size, scale with transform for mobile landscape
    const [isPipVisible, setIsPipVisible] = useState(true);
    const [isPipInPopup, setIsPipInPopup] = useState(false);
    const [popupWindow, setPopupWindow] = useState<Window | null>(null);
    const [videoSize, setVideoSize] = useState({ width: 320, height: 180 });
    const [isResizing, setIsResizing] = useState(false);
    const [isOverResizeHandle, setIsOverResizeHandle] = useState(false);
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const [isHovered, setIsHovered] = useState(false);
    const [isResizeCooldown, setIsResizeCooldown] = useState(false);
    const [pipZoom, setPipZoom] = useState<number | undefined>(initialZoom);
    const videoRef = useRef<HTMLDivElement>(null);
    const resizeCooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pipMapRef = useRef<MapType | null>(null);

    const openPipPopup = useCallback(() => {
      if (popupWindow && !popupWindow.closed) {
        popupWindow.focus();
        return;
      }

      const popup = window.open(
        '',
        'pipVideo',
        'width=480,height=270,scrollbars=no,resizable=yes,status=no,location=no,toolbar=no,menubar=no'
      );

      if (popup) {
        popup.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${t('crew_view.live_video_feed')}</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              background: black;
              overflow: hidden;
              font-family: 'Open Sans', sans-serif;
            }
            #root {
              width: 100vw;
              height: 100vh;
            }
            .close-button {
              position: absolute;
              top: 8px;
              right: 8px;
              background: rgba(0,0,0,0.5);
              color: white;
              border: none;
              padding: 8px;
              cursor: pointer;
              border-radius: 4px;
              font-size: 16px;
              z-index: 1000;
            }
            .close-button:hover {
              background: rgba(0,0,0,0.7);
            }
          </style>
        </head>
        <body>
          <script>
            window.addEventListener('beforeunload', function() {
              if (window.opener && !window.opener.closed) {
                window.opener.postMessage({ type: 'popupClosed' }, '*');
              }
            });
          </script>
        </body>
        </html>
      `);
        popup.document.close();

        const container = popup.document.createElement('div');
        container.id = 'root';
        popup.document.body.appendChild(container);
        const root = createRoot(container);
        root.render(
          isFullscreenVideo ? (
            <div style={{ width: '100%', height: '100%' }}>
              <SimpleVideoStream
                key={`${videoUrl}-${autoPlayout}-${strictFrames}`}
                url={videoUrl}
                fitMode={fitMode}
                onFitModeChange={onFitModeChange}
                playout={crewPlayout(autoPlayout, strictFrames)}
              />
            </div>
          ) : (
            <MapDroneView
              liveLine={liveLine}
              markers={markerImages}
              mapRef={mapRef}
              customZoomControls
              initialZoom={initialZoom}
              hideControls
            />
          )
        );
        setPopupWindow(popup);
        setIsPipInPopup(true);
      }
    }, [
      liveLine,
      markerImages,
      isFullscreenVideo,
      popupWindow,
      videoUrl,
      mapRef,
      t,
      initialZoom,
      fitMode,
      onFitModeChange,
      autoPlayout,
      strictFrames
    ]);

    const togglePipVisibility = useCallback(() => {
      if (isPipInPopup) {
        if (popupWindow && !popupWindow.closed) {
          popupWindow.close();
        }
        setIsPipInPopup(false);
        setPopupWindow(null);
        setIsPipVisible(true);
      } else {
        setIsPipVisible(false);
        openPipPopup();
      }
    }, [isPipInPopup, popupWindow, openPipPopup]);

    const togglePipHide = useCallback(() => {
      setIsPipVisible(!isPipVisible);
    }, [isPipVisible]);

    const handleClick = useCallback((): void => {
      if (isResizing || isOverResizeHandle || isResizeCooldown) {
        return;
      }
      onChangeView();
    }, [isResizing, isOverResizeHandle, isResizeCooldown, onChangeView]);

    const handleResizeStart = useCallback(
      (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if ('nativeEvent' in e) {
          e.nativeEvent.stopImmediatePropagation();
        }
        setIsResizing(true);
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        setResizeStart({
          x: clientX,
          y: clientY,
          width: videoSize.width,
          height: videoSize.height
        });
      },
      [videoSize]
    );

    const handleResizeMove = useCallback(
      (e: MouseEvent | TouchEvent) => {
        if (!isResizing) return;

        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const deltaX = clientX - resizeStart.x;
        const aspectRatio = 16 / 9;
        // Invert deltaX since resize handle is now on the left side (moving right decreases width)
        const newWidth = Math.max(160, Math.min(756, resizeStart.width - deltaX));
        const newHeight = Math.max(90, Math.min(450, newWidth / aspectRatio));

        setVideoSize({ width: newWidth, height: newHeight });
      },
      [isResizing, resizeStart]
    );

    const handleResizeEnd = useCallback(() => {
      setIsResizing(false);

      // Resize map immediately when resize ends
      if (!isFullscreenVideo && mapRef.current) {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          mapRef.current?.resize();
        });
      }

      // Set cooldown period to prevent accidental view changes after resizing
      setIsResizeCooldown(true);

      // Clear any existing timeout
      if (resizeCooldownTimeoutRef.current) {
        clearTimeout(resizeCooldownTimeoutRef.current);
      }

      // Clear cooldown after 300 milliseconds
      resizeCooldownTimeoutRef.current = setTimeout(() => {
        setIsResizeCooldown(false);
        resizeCooldownTimeoutRef.current = null;
      }, 300);
    }, [isFullscreenVideo, mapRef]);

    useEffect(() => {
      if (isResizing) {
        // Prevent default touch behavior to avoid scrolling while resizing
        const preventDefault = (e: TouchEvent) => {
          e.preventDefault();
        };

        const handleTouchMove = (e: TouchEvent) => {
          preventDefault(e);
          handleResizeMove(e);
        };

        document.addEventListener('mousemove', handleResizeMove);
        document.addEventListener('mouseup', handleResizeEnd);
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleResizeEnd);
        document.addEventListener('touchcancel', handleResizeEnd);

        return () => {
          document.removeEventListener('mousemove', handleResizeMove);
          document.removeEventListener('mouseup', handleResizeEnd);
          document.removeEventListener('touchmove', handleTouchMove);
          document.removeEventListener('touchend', handleResizeEnd);
          document.removeEventListener('touchcancel', handleResizeEnd);
        };
      }
    }, [isResizing, handleResizeMove, handleResizeEnd]);

    useEffect(() => {
      const handleMessage = (event: MessageEvent): void => {
        if (event.data.type === 'popupClosed') {
          setIsPipInPopup(false);
          setPopupWindow(null);
          setIsPipVisible(true);
        }
      };

      window.addEventListener('message', handleMessage);

      const checkPopup = setInterval(() => {
        if (popupWindow && popupWindow.closed) {
          setIsPipInPopup(false);
          setPopupWindow(null);
          setIsPipVisible(true);
        }
      }, 1000);

      return () => {
        window.removeEventListener('message', handleMessage);
        clearInterval(checkPopup);
        if (popupWindow && !popupWindow.closed) {
          popupWindow.close();
        }
      };
    }, [popupWindow]);

    // Cleanup cooldown timeout on unmount
    useEffect(() => {
      return () => {
        if (resizeCooldownTimeoutRef.current) {
          clearTimeout(resizeCooldownTimeoutRef.current);
        }
      };
    }, []);

    // Get current zoom from main map when switching to map view in PiP
    useEffect(() => {
      if (!isFullscreenVideo && mapRef.current) {
        const currentZoom = mapRef.current.getZoom();
        if (currentZoom !== undefined) {
          setPipZoom(currentZoom);
        }
      }
    }, [isFullscreenVideo, mapRef]);

    // Resize map when container size changes (for picture mode)
    useEffect(() => {
      if (!isFullscreenVideo && mapRef.current) {
        // Use a small delay to ensure DOM has updated
        const timeoutId = setTimeout(() => {
          mapRef.current?.resize();
        }, 0);
        return () => clearTimeout(timeoutId);
      }
    }, [videoSize, isFullscreenVideo, mapRef]);

    return (
      <>
        {!isPipInPopup && (
          <Box
            ref={videoRef}
            className='absolute bottom-0 right-0 z-20 overflow-hidden cursor-pointer'
            style={{
              zIndex: 99999
            }}
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
              bgcolor: 'background.level1',
              width: `${videoSize.width}px`,
              height: `${videoSize.height}px`,
              opacity: isPipVisible ? 1 : 0,
              visibility: isPipVisible ? 'visible' : 'hidden',
              transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
              // Mobile landscape: position at top right, bottom aligned with dock top
              '@media (orientation: landscape) and (min-width: 910px) and (max-width: 1024px)': {
                right: '0px',
                left: 'auto',
                transform: 'scale(0.766)',
                transformOrigin: 'top right',
                bottom: '-10%'
              },
              // iPad landscape with height > 750px: use -6% instead of -10%
              '@media (orientation: landscape) and (min-width: 910px) and (max-width: 1024px) and (min-height: 751px)':
                {
                  bottom: '-6%'
                },
              '@media (orientation: landscape) and (max-width: 909px)': {
                right: '0px',
                left: 'auto',
                transform: 'scale(0.766)',
                transformOrigin: 'top right'
              }
            }}
          >
            <Box
              className='absolute bottom-0 right-0 z-50 cursor-pointer'
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                togglePipHide();
              }}
              onMouseEnter={() => setIsHovered(true)}
              sx={{
                opacity: isHovered ? 1 : 0,
                transition: 'opacity 0.2s ease-in-out'
              }}
            >
              <PiPHide className='w-8 h-8' style={{ transform: 'scaleX(-1)' }} />
            </Box>

            {isFullscreenVideo && (
              <Box
                className='absolute top-0 right-0 z-50'
                onMouseEnter={() => setIsHovered(true)}
                sx={{
                  opacity: isHovered ? 1 : 0,
                  transition: 'opacity 0.2s ease-in-out'
                }}
              >
                <PiPPopup
                  onClick={(e: React.MouseEvent<SVGSVGElement>) => {
                    e.stopPropagation();
                    togglePipVisibility();
                  }}
                  className='w-8 h-8 cursor-pointer'
                  style={{ transform: 'scaleX(-1)' }}
                />
              </Box>
            )}

            <Box
              className='absolute top-0 left-0 z-50'
              onMouseDown={handleResizeStart}
              onTouchStart={handleResizeStart}
              onMouseEnter={(e) => {
                e.stopPropagation();
                setIsOverResizeHandle(true);
                setIsHovered(true);
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
                setIsOverResizeHandle(false);
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              sx={{
                opacity: isHovered ? 1 : 0,
                transition: 'opacity 0.2s ease-in-out',
                // Show resize handle on mobile and ensure proper touch target size
                '@media (hover: none) and (pointer: coarse)': {
                  opacity: 1,
                  minWidth: '44px',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  touchAction: 'none' // Prevent default touch behaviors
                }
              }}
            >
              <PipResize className='w-8 h-8' style={{ transform: 'rotate(270deg)' }} />
            </Box>

            <Box
              className='relative flex justify-center items-center w-full h-full'
              sx={{
                '& > div': {
                  width: '100%',
                  height: '100%'
                }
              }}
            >
              {isFullscreenVideo ? (
                <SimpleVideoStream
                  key={`${videoUrl}-${autoPlayout}-${strictFrames}`}
                  url={videoUrl}
                  fitMode={fitMode}
                  onFitModeChange={onFitModeChange}
                  playout={crewPlayout(autoPlayout, strictFrames)}
                />
              ) : (
                <MapDroneView
                  key={`pip-map-${pipZoom ?? initialZoom}`}
                  liveLine={liveLine}
                  markers={markerImages}
                  mapRef={pipMapRef}
                  customZoomControls
                  initialZoom={pipZoom ?? initialZoom}
                  hideControls
                  inPipMode
                />
              )}
            </Box>
          </Box>
        )}
        {!isPipVisible && (
          <Box
            className='absolute bottom-[13%] sm:bottom-0 right-0 z-30 rounded-md cursor-pointer'
            onClick={togglePipHide}
            sx={{
              bgcolor: 'background.level1',
              opacity: 0.6,
              transition: 'opacity 0.3s ease-in-out'
            }}
          >
            <PiPButtonRight className='w-8 h-8' style={{ transform: 'scaleX(-1)' }} />
          </Box>
        )}
      </>
    );
  }
);

VideoPiPContainer.displayName = 'VideoPiPContainer';

export default VideoPiPContainer;
